import React, { useEffect, useRef, useState } from 'react';
import { KeyRound, Loader2, Mail } from 'lucide-react';
import { initSso } from '@/lib/api/sso';
import { logger } from '@/lib/utils/logger';
import { getErrorMessage } from '@/lib/utils/errorHandling';

interface SSOProvidersProps {
  /** Mode hint. Only `signin` renders an SSO affordance today. */
  mode?: 'signin' | 'signup' | 'invite-accept';
  /**
   * External prompt (e.g. from a `sso_required` password-login error) that
   * forces the SSO control open and shows a steering message. When set, the
   * control expands and scrolls into view.
   */
  prompt?: string | null;
  /** Prefill the SSO email input, typically the email typed into the login form. */
  initialEmail?: string;
}

/**
 * SSO (OIDC) sign-in affordance.
 *
 * In `signin` mode this renders a "Sign in with SSO" control that reveals an
 * email input. On submit it calls the backend SSO init endpoint: if the email's
 * domain has SSO configured the browser is redirected to the identity provider,
 * otherwise an inline message steers the user back to password login.
 *
 * Other modes render nothing (the original placeholder behavior).
 *
 * NOTE: this component is mounted inside the login `<form>`, so every button is
 * `type="button"` and the email input intercepts Enter — otherwise a keystroke
 * here would submit the surrounding password form.
 */
export const SSOProviders: React.FC<SSOProvidersProps> = ({
  mode = 'signin',
  prompt,
  initialEmail,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const promptOpen = Boolean(prompt);
  const isOpen = expanded || promptOpen;

  // When an external prompt (sso_required) arrives, open the control, prefill
  // the email the user already typed, and bring it into view.
  useEffect(() => {
    if (promptOpen) {
      if (initialEmail) {
        setEmail((current) => current || initialEmail);
      }
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [promptOpen, initialEmail]);

  if (mode !== 'signin') {
    return null;
  }

  const submit = async (): Promise<void> => {
    const trimmed = email.trim();
    if (trimmed === '' || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setMessage(null);
    try {
      const result = await initSso(trimmed);
      if (result.ssoAvailable && result.redirectUrl) {
        window.location.assign(result.redirectUrl);
        return;
      }
      setMessage('No SSO is configured for that email. Sign in with your password instead.');
    } catch (error) {
      logger.error('SSO init failed:', error);
      setMessage(`Could not start SSO sign-in. Please try again. (${getErrorMessage(error)})`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={containerRef} className="mb-6">
      {!isOpen ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full px-4 py-2.5 border border-zinc-300 dark:border-zinc-600 rounded-lg text-sm font-medium text-primary hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-colors flex items-center justify-center gap-2"
          data-testid="sso-reveal-button"
        >
          <KeyRound className="h-4 w-4 text-sky-600 dark:text-sky-400" />
          <span>Sign in with SSO</span>
        </button>
      ) : (
        <div className="space-y-3" data-testid="sso-form">
          {prompt && (
            <div
              className="p-3 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg"
              data-testid="sso-prompt"
            >
              <p className="text-sm text-sky-700 dark:text-sky-300">{prompt}</p>
            </div>
          )}
          <div>
            <label htmlFor="sso-email" className="form-label">
              Work email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-zinc-400 dark:text-zinc-500" />
              </div>
              <input
                id="sso-email"
                type="email"
                name="ssoEmail"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  // The outer login form would otherwise submit on Enter.
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void submit();
                  }
                }}
                className="form-input pl-11!"
                placeholder="you@example.com"
                data-testid="sso-email-input"
              />
            </div>
          </div>
          {message && (
            <p className="text-sm text-secondary" data-testid="sso-message">
              {message}
            </p>
          )}
          <button
            type="button"
            onClick={() => void submit()}
            disabled={isSubmitting || email.trim() === ''}
            className="btn-filled-sky w-full py-2.5 flex items-center justify-center gap-2"
            data-testid="sso-submit-button"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Redirecting...</span>
              </>
            ) : (
              <span>Continue with SSO</span>
            )}
          </button>
        </div>
      )}

      <div className="flex items-center gap-3 mt-6" aria-hidden="true">
        <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
        <span className="text-xs text-secondary uppercase tracking-wide">or</span>
        <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
      </div>
    </div>
  );
};
